import { ChangeEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import { addDays } from 'date-fns';
import { IBalloonVehicle, IResidualValues } from 'modules/core/types/balloon';
import { IField, IUser } from 'modules/core/types';
import { costFormatter, getValidDateOrToday, thousandFormatter } from 'modules/core/utils';
import { ColumnWidth } from 'modules/core/enums';
import { validateMaxAvailableValue, validateRequiredMinAndMaxValue } from 'modules/core/validators';
import { EMPTY_STRING } from 'modules/core/constants';
import { FormRow } from 'modules/core/components';
import { DeepMap, FieldError } from 'react-hook-form';
import { IDealSourceData, IDealSourceDataFlags } from 'modules/core/types/dealSource';
import { IRegionControls } from 'modules/core/types/region';
import { useIsAllyLender } from 'modules/core/hooks/useIsAllyLender';
import { useIsAmeriCreditLender } from 'modules/core/hooks/useIsAmeriCreditLender';
import { useIsMtbLender } from 'modules/core/hooks/useIsMtbLender';

import {
  ALLY_THIRD_MW_ROW_FIELDS,
  ALLY_THIRD_MW_ROW_LABELS,
  APPLY_CPO_CHECKBOX_FIELD,
  BALLOON_FIELDS,
  BALLOON_LABELS,
  FIRST_MW_ROW_FIELDS,
  FIRST_MW_ROW_LABELS,
  SECOND_MW_ROW_FIELDS,
  SECOND_MW_ROW_LABELS,
  THIRD_MW_ROW_FIELDS,
  THIRD_MW_ROW_LABELS,
} from '../consts/fieldConfig.const';
import { FieldHint, FormControlFeedback, WarningFieldHint } from '../styles';
import { AVAILABLE_DEAL_STRUCTURE_STATUSES, CALCULATION_FIELD_NAMES, HISTORY_FIELD_NAMES } from '../consts/consts';
import { getAnnualMileages, updateBalloonPaymentFormValues } from '../balloonPayment/BalloonPayment.service';
import { LOAN_VARIABLES_FIELDS_NAMES } from '../consts/fieldNames.const';
import { IAvailableDealStructureStatus } from '../types';
import { GuidebookLabelWithIcon } from '../components/common/Label/GuidebookLabelWithIcon';

const REQUIRED_FIELDS = [
  LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE,
  LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE,
  LOAN_VARIABLES_FIELDS_NAMES.TAXES,
  LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM,
  LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE,
  LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM,
  LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE,
  LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE,
];

export const useGeneratedFields = (
  isLender: boolean,
  balloonToggle: boolean,
  isModalSalesFeesDialogActive: boolean,
  isIncomeDialogActive: boolean,
  isNetTradeInDialogOpen: boolean,
  cancelDebouncedRecalculate: () => void,
  isFieldChanged: (name: string) => boolean,
  setHistoryCallback: () => void,
  getValues: any,
  balloonVehicleInfo: IBalloonVehicle[],
  balloonVehicleEngines: string[],
  balloonMaxOdometers: any,
  balloonResidualValues: IResidualValues | null,
  setValue: any,
  overrideFormHistoryValues: any,
  trigger: (name?: string | string[] | undefined) => Promise<boolean>,
  handleChangeCallback: (event?: ChangeEvent<HTMLInputElement>) => void,
  clearErrors: (name?: string | string[] | undefined) => void,
  termsMatchedWithBalloon: number[],
  loanTermsList: number[],
  handleSalesFeesDialogOpen: () => void,
  handleIncomeDialogOpen: () => void,
  handleNetTradeInDialogOpen: () => void,
  handleBackAddsDialogOpen: () => void,
  lenderControls: {
    maxVSC: number;
    maxGap: number;
    showRates: boolean;
  },
  maxBackEnd: any,
  loanVariables: any,
  errors: DeepMap<any, FieldError>,
  user: IUser | null,
  manuallyCreated: boolean,
  maxPaymentValue: () => number,
  payment: any,
  dealSourceData: IDealSourceData,
  afgTrim: any,
  maxMileage: number,
  regionControls: IRegionControls,
  guidebookValue: any,
  defaultParticipation: number,
  maxParticipation: number | undefined,
  availableDealStructureStatus: IAvailableDealStructureStatus,
  isAdjustDealModalAnimated: boolean,
  isTaxesAnimated: boolean,
  isSpinerAnimated: boolean,
  isTermAnimated: boolean,
  dealSourceDataFlags: IDealSourceDataFlags,
  isSalespersonLimitedView: boolean,
  isShopByCallbackMode: boolean,
  contractDate: Date,
  isAddendumActive: boolean,
  setAddendumStep: Function,
  resetDealSourceDataFlags: Function,
  firstPaymentDaysRange: { default: number; min: number; max: number },
  submittedTermMismatch?: number,
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isAllyLender = useIsAllyLender(isLender);
  const isAmericreditLender = useIsAmeriCreditLender(isLender);
  const isMtbLender = useIsMtbLender(isLender);
  const enteredBookValue = Number(guidebookValue);
  const isCpoEligibilityStale =
    Boolean(loanVariables?.is_cpo_eligible) &&
    !(enteredBookValue > 0 && enteredBookValue === loanVariables?.guidebook_value);
  const shouldHideElement = useCallback(
    (name: string) => isSalespersonLimitedView && name === LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE,
    [isSalespersonLimitedView],
  );

  const FILTERED_REQUIRED_FIELDS = useMemo(() => REQUIRED_FIELDS.filter((field) => !shouldHideElement(field)), [
    shouldHideElement,
  ]);

  const changeCallback = useCallback(
    async (name, component, event = {}) => {
      setAddendumStep(0);
      dispatch(resetDealSourceDataFlags());

      if (isSalespersonLimitedView && name === LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE) {
        return;
      }

      const nameKey = name.split('.')[1];

      if (HISTORY_FIELD_NAMES.includes(name) && isFieldChanged(nameKey)) {
        setHistoryCallback();
      }

      if (name === LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE) {
        const newContractDate = getValues(LOAN_VARIABLES_FIELDS_NAMES.CONTRACT_DATE);

        if (newContractDate) {
          const newFirstPaymentDate = addDays(newContractDate, firstPaymentDaysRange.default);

          setValue(LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE, newFirstPaymentDate);
          overrideFormHistoryValues({ firstPaymentDate: newFirstPaymentDate });
          await trigger(LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE);
        }
      }

      if (name === LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM) {
        const { afgTrim } = getValues().loanVariablesForm;
        const actualBalloonVehicle = balloonVehicleInfo[afgTrim.value];

        updateBalloonPaymentFormValues(actualBalloonVehicle, balloonVehicleInfo, setValue, overrideFormHistoryValues);
        await trigger();
      }

      if (name === LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO) {
        handleChangeCallback({ target: { name } } as any);

        return;
      }

      if (CALCULATION_FIELD_NAMES.includes(nameKey)) {
        handleChangeCallback(event);
      }
    },
    [
      setAddendumStep,
      setHistoryCallback,
      isFieldChanged,
      handleChangeCallback,
      getValues,
      balloonVehicleInfo,
      setValue,
      overrideFormHistoryValues,
      trigger,
      isSalespersonLimitedView,
      dispatch,
      resetDealSourceDataFlags,
      firstPaymentDaysRange.default,
    ],
  );

  const dropdownToggleCallback = useCallback(
    (isOpen, event, name, component) => {
      const target = event?.originalEvent?.target;
      const isBalloonToggle = target?.name === LOAN_VARIABLES_FIELDS_NAMES.BALLOON_TOGGLE;
      const isAnotherDropdownElement = target?.className?.includes?.('dropdown') && target?.name !== name;
      const shouldTriggerChange =
        !isModalSalesFeesDialogActive &&
        !isIncomeDialogActive &&
        !isNetTradeInDialogOpen &&
        !isBalloonToggle &&
        !isAnotherDropdownElement;

      if (isOpen) {
        cancelDebouncedRecalculate();
      } else if (shouldTriggerChange) {
        changeCallback(name, component);
      }
    },
    [
      isModalSalesFeesDialogActive,
      isIncomeDialogActive,
      isNetTradeInDialogOpen,
      cancelDebouncedRecalculate,
      changeCallback,
    ],
  );

  const mapOptionsByFieldName = useMemo(() => {
    const actualLoanTermsList = balloonToggle ? termsMatchedWithBalloon : loanTermsList;
    const loanTermOptions =
      actualLoanTermsList.length >= 1 ? actualLoanTermsList.map((value) => ({ title: value, value: value })) : [];
    const annualMileages = getAnnualMileages(balloonResidualValues);
    const annualMileageOptions = annualMileages.map((item) => ({
      title: thousandFormatter(item),
      value: item,
    }));
    const afgTrimOptons =
      balloonVehicleInfo.length >= 1
        ? balloonVehicleInfo.map((item, index) => ({ title: item?.style, value: index }))
        : [];
    const engineOptions = balloonVehicleEngines.map((engine) => ({
      title: engine,
      value: engine,
    }));

    return new Map<string, any>([
      [LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM, loanTermOptions],
      [LOAN_VARIABLES_FIELDS_NAMES.ENGINE, engineOptions],
      [LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE, annualMileageOptions],
      [LOAN_VARIABLES_FIELDS_NAMES.AFG_TRIM, afgTrimOptons],
    ]);
  }, [
    loanTermsList,
    balloonResidualValues,
    balloonVehicleEngines,
    balloonVehicleInfo,
    balloonToggle,
    termsMatchedWithBalloon,
  ]);

  const mapOnClickFunctionByFieldName = useMemo(
    () =>
      new Map<string, VoidFunction>([
        [LOAN_VARIABLES_FIELDS_NAMES.TOTAL_SALES_FEES, handleSalesFeesDialogOpen],
        [LOAN_VARIABLES_FIELDS_NAMES.MONTHLY_INCOME, handleIncomeDialogOpen],
        [LOAN_VARIABLES_FIELDS_NAMES.NET_TRADE_IN, handleNetTradeInDialogOpen],
        ...(isAllyLender
          ? [[LOAN_VARIABLES_FIELDS_NAMES.VSC, handleBackAddsDialogOpen] as [string, VoidFunction]]
          : []),
      ]),
    [
      handleSalesFeesDialogOpen,
      handleIncomeDialogOpen,
      handleNetTradeInDialogOpen,
      handleBackAddsDialogOpen,
      isAllyLender,
    ],
  );

  const getGapLimit = useCallback(
    ({ t, regionControls }: any): any => {
      let errorMessage = '';
      let maxValue = 0;

      maxValue = isLender ? lenderControls.maxGap : regionControls.gapLimit;
      errorMessage = t('components.RACAffordabilityEngine.vscAndGapLimits.gapLimitMessage', {
        maxGap: costFormatter(maxValue, false),
      });

      return { maxValue, errorMessage };
    },
    [isLender, lenderControls.maxGap],
  );

  const getVscLimit = useCallback(
    ({ t, guidebookValue, regionControls }: any): any => {
      let errorMessage = '';
      let maxValue = 0;

      if (isLender) {
        maxValue = maxBackEnd;
        errorMessage = t('components.RACAffordabilityEngine.vscAndGapLimits.backEndLimitMessage', {
          maxBackEnd: costFormatter(maxBackEnd, false),
        });

        return { maxValue, errorMessage };
      }

      if (!!regionControls.vscLimitVehicleValue && !!regionControls.vscLimitBelow && !!regionControls.vscLimitAbove) {
        if (guidebookValue && guidebookValue < regionControls.vscLimitVehicleValue) {
          maxValue = regionControls.vscLimitBelow;
        } else {
          maxValue = regionControls.vscLimitAbove;
        }

        errorMessage = t('components.RACAffordabilityEngine.vscAndGapLimits.vscLimitMessage', {
          maxVSC: costFormatter(maxValue, false),
        });
      }

      return { maxValue, errorMessage };
    },
    [isLender, maxBackEnd],
  );

  const generateFields = useCallback(
    (fields: IField[]) =>
      fields.map(
        ({
          name,
          component: Component,
          controlProps: originalControlProps,
          label,
          isVerticalAlign,
          textLabel,
        }: IField) => {
          const controlProps = originalControlProps ? { ...originalControlProps } : undefined;
          const cpoIsRequired = Boolean(loanVariables?.force_cpo);

          if (name === LOAN_VARIABLES_FIELDS_NAMES.APPLY_CPO && controlProps) {
            // The form value stays the dealer's choice. The box is drawn checked, and locked,
            // only while this quote still meets Ally's below-book rule.
            controlProps.disabled = cpoIsRequired;
            controlProps.forceChecked = cpoIsRequired;
          }

          // Return a placeholder-column for hidden fields to keep the layout structure
          if (shouldHideElement(name)) {
            return <Col key={name} className="placeholder" />;
          }

          if (
            name === LOAN_VARIABLES_FIELDS_NAMES.GAP &&
            (isMtbLender || !(isLender ? lenderControls.maxGap : regionControls.gapLimit))
          ) {
            return <Col key={name} />;
          }

          const adornmentOnClick = mapOnClickFunctionByFieldName.get(name);
          const options = mapOptionsByFieldName.get(name);
          const isGuideBookField = name === LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE;

          const shouldShowGuidebookInfoIcon =
            isGuideBookField && Boolean(loanVariables?.guidebook_short) && !!user?.isWithInventory && !manuallyCreated;

          let inputLabel = isGuideBookField ? loanVariables?.guidebook_short ?? loanVariables?.guidebook : label;
          let fieldWidth = {
            fieldWidth: controlProps?.fieldWidthForce ? (controlProps.fieldWidth as ColumnWidth) : 'adjustDeal',
          };

          const nameKey = name.split('.')[1];
          const value = getValues(name);

          let message = '';
          let isInvalid = false;

          // Ally, AmeriCredit, and M&T Bank drop the ¹ ² ³ footnotes on Step 1.
          // AmeriCredit and M&T also drop the CPOV mark.
          const dropsStepOneSuperscripts = isAllyLender || isAmericreditLender || isMtbLender;

          if (dropsStepOneSuperscripts && name === LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM) {
            inputLabel = t('components.loanVariablesModal.fields.loanTerm');
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.VSC && isLender) {
            inputLabel = t(
              dropsStepOneSuperscripts
                ? 'components.loanVariablesModal.fields.backEnd'
                : 'components.loanVariablesModal.fields.backEndWithNumber',
            );
          }

          if (dropsStepOneSuperscripts && name === LOAN_VARIABLES_FIELDS_NAMES.GAP) {
            inputLabel = t('components.loanVariablesModal.fields.gap');
          }

          if (
            name === LOAN_VARIABLES_FIELDS_NAMES.CURRENT_PAYMENT &&
            (dropsStepOneSuperscripts || (isMtbLender && loanVariables?.is_requested_payment))
          ) {
            inputLabel = t(
              loanVariables?.is_requested_payment
                ? 'components.loanVariablesModal.fields.requestedPayment'
                : 'components.loanVariablesModal.fields.currentPayment',
            );
          }

          if (isGuideBookField) {
            if (!user?.isWithInventory || manuallyCreated) {
              inputLabel = t('components.loanVariablesModal.fields.bookValue');
            }

            // REP-23: Ally dropped ¹–³ footnotes, so CPOV is the only remaining mark and uses ¹.
            if (!!loanVariables?.ally_cpo && !isAmericreditLender && !isMtbLender) {
              inputLabel = `${inputLabel}${isAllyLender ? '¹' : '⁴'}`;
            }
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.PAYMENT && controlProps) {
            const maxValue = maxPaymentValue();
            const validatePayment =
              isLender && isShopByCallbackMode ? () => undefined : validateRequiredMinAndMaxValue(maxValue);
            const maxValueMessage = t('components.loanVariablesModal.errorMessages.maxValue', {
              maxValue: costFormatter(maxValue, false),
            });

            isInvalid = validatePayment(payment) === EMPTY_STRING;
            controlProps.validate = validatePayment;

            if (value) {
              if (isLender && dealSourceData?.finance_details?.vsc_payment_allowance && !isInvalid) {
                message = t('components.loanVariablesModal.infoMessages.includeVscAllowance');
              } else if (isInvalid) {
                message = maxValueMessage;
              }
            } else {
              message = t('components.loanVariablesModal.errorMessages.requiredField');
            }
          }

          if (
            FILTERED_REQUIRED_FIELDS.includes(name) &&
            controlProps?.validate &&
            (controlProps.validate as Function)(value) === EMPTY_STRING
          ) {
            isInvalid = true;
            message = t('components.loanVariablesModal.errorMessages.requiredField');
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.MILEAGE && controlProps?.validate) {
            const actualBalloonVehicle = balloonVehicleInfo[afgTrim.value];
            const balloonMaxOdometer = balloonMaxOdometers[String(actualBalloonVehicle?.modelYear)];
            const maxMileageValue =
              balloonToggle && actualBalloonVehicle ? Math.min(maxMileage, balloonMaxOdometer) : maxMileage;
            const validateMileage = (value: number) =>
              Number.isNaN(value) || (isLender && value > maxMileageValue) ? EMPTY_STRING : undefined;

            isInvalid = validateMileage(value) === EMPTY_STRING;
            controlProps.validate = validateMileage;

            if (isInvalid) {
              message = t(
                isLender && value > maxMileageValue
                  ? 'components.loanVariablesModal.errorMessages.maxValue'
                  : 'components.loanVariablesModal.errorMessages.requiredField',
                { maxValue: thousandFormatter(maxMileageValue) },
              );
            }
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.GAP && controlProps) {
            const { maxValue, errorMessage } = getGapLimit({ t, regionControls });

            if (!!maxValue) {
              isInvalid = validateMaxAvailableValue(maxValue)(value) === EMPTY_STRING;
              controlProps.validate = validateMaxAvailableValue(maxValue);
              message = errorMessage;
            }
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.VSC && controlProps) {
            const { maxValue, errorMessage } = getVscLimit({ t, guidebookValue, regionControls });

            if (!!maxValue) {
              isInvalid = validateMaxAvailableValue(maxValue)(value) === EMPTY_STRING;
              controlProps.validate = validateMaxAvailableValue(maxValue);

              if (!isLender || isInvalid) message = errorMessage;
            }
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.PARTICIPATION) {
            if (!isLender || !defaultParticipation || !maxParticipation) {
              return <Col></Col>;
            } else {
              const maxValue = maxParticipation || 0;
              const errorMessage = t('components.loanVariablesModal.errorMessages.maxParticipationIs', {
                maxParticipation: maxValue,
              });

              if ((defaultParticipation || defaultParticipation === 0) && !!maxValue && controlProps) {
                message = errorMessage;
                isInvalid = validateMaxAvailableValue(maxValue)(value) === EMPTY_STRING;
                controlProps.validate = validateMaxAvailableValue(maxValue);
              }
            }
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.CURRENT_PAYMENT) {
            isInvalid = availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY;
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.TAXES && controlProps) {
            controlProps.isComponentAnimated = isAdjustDealModalAnimated && isTaxesAnimated;
            controlProps.isSpinerAnimated = isSpinerAnimated && isTaxesAnimated;

            if (isAdjustDealModalAnimated) {
              isInvalid = false;
              message = '';
            }
          }

          if (name === LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM && controlProps) {
            controlProps.isComponentAnimated = isAdjustDealModalAnimated && isTermAnimated;
            controlProps.isSpinerAnimated = isSpinerAnimated && isTermAnimated;

            if (isAdjustDealModalAnimated && isTermAnimated) {
              isInvalid = false;
              message = '';
            }

            controlProps.isWarning = !!submittedTermMismatch && !isInvalid;
          }

          if (
            controlProps &&
            (name === LOAN_VARIABLES_FIELDS_NAMES.CURRENT_PAYMENT ||
              name === LOAN_VARIABLES_FIELDS_NAMES.TOTAL_AMOUNT_FINANCED ||
              name === LOAN_VARIABLES_FIELDS_NAMES.LINE_THREE ||
              name === LOAN_VARIABLES_FIELDS_NAMES.BALLOON_PAYMENT)
          ) {
            controlProps.isComponentAnimated = isAdjustDealModalAnimated;
            controlProps.isSpinerAnimated = isSpinerAnimated && isAdjustDealModalAnimated;
            controlProps.hideValue =
              availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO;
          }

          if (controlProps && name === LOAN_VARIABLES_FIELDS_NAMES.FIRST_PAYMENT_DATE) {
            const baseContractDate = getValidDateOrToday(contractDate);

            controlProps.minDate = addDays(baseContractDate, firstPaymentDaysRange.min);
            controlProps.maxDate = addDays(baseContractDate, firstPaymentDaysRange.max);
          }

          const blurCallback = isFieldChanged(nameKey) ? () => setHistoryCallback() : undefined;
          const isDisabled = () => {
            if (isLender) {
              return (
                (name === LOAN_VARIABLES_FIELDS_NAMES.GAP && !lenderControls.maxGap) ||
                (name === LOAN_VARIABLES_FIELDS_NAMES.VSC && !lenderControls.maxVSC) ||
                (name === LOAN_VARIABLES_FIELDS_NAMES.ROUTE_ONE_USER_ID && !!user?.routeOneUserId) ||
                Boolean(controlProps?.disabled)
              );
            } else {
              return (
                (name === LOAN_VARIABLES_FIELDS_NAMES.GAP && !regionControls.gapLimit) ||
                Boolean(controlProps?.disabled)
              );
            }
          };
          const componentClass = () => {
            let className = '';

            if (
              !message &&
              name !== LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE &&
              name !== LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM &&
              name !== LOAN_VARIABLES_FIELDS_NAMES.ROUTE_ONE_USER_ID
            ) {
              className += 'mg-btm';
            }

            if ((isAdjustDealModalAnimated || isAddendumActive) && adornmentOnClick) className += ' disabled-adornment';

            return !className ? undefined : className;
          };

          const componentContent = (
            <>
              <Component
                {...controlProps}
                options={options}
                name={name}
                textLabel={textLabel}
                endAdornmentOnClick={isAdjustDealModalAnimated || isAddendumActive ? undefined : adornmentOnClick}
                blurCallback={blurCallback}
                changeCallback={(event) => changeCallback(name, Component, event)}
                isInvalid={isInvalid}
                clickCallback={controlProps?.readonly ? adornmentOnClick : undefined}
                disabled={
                  isAdjustDealModalAnimated || isAddendumActive || dealSourceDataFlags?.isLoading ? true : isDisabled()
                }
                className={componentClass()}
                onToggle={(isOpen, event) => dropdownToggleCallback(isOpen, event, name, Component)}
              />
              {message && (
                <FormControlFeedback
                  type="invalid"
                  className={`${isInvalid ? 'd-block' : 'd-none'}`}
                  $isInvalid={isInvalid}
                  isItalic={isInvalid}
                  style={{ marginBottom: '4px' }}
                >
                  {message}
                </FormControlFeedback>
              )}
              {(isAllyLender || isMtbLender) && name === LOAN_VARIABLES_FIELDS_NAMES.PAYMENT && !isInvalid && (
                <FieldHint data-testid="exclude-back-adds-hint">
                  {t('components.loanVariablesModal.infoMessages.excludeBackAdds')}
                </FieldHint>
              )}
              {name === LOAN_VARIABLES_FIELDS_NAMES.LOAN_TERM && controlProps?.isWarning && (
                <WarningFieldHint data-testid="submitted-term-hint">
                  {t('components.loanVariablesModal.infoMessages.submittedTerm', { term: submittedTermMismatch })}
                </WarningFieldHint>
              )}
              {isGuideBookField &&
                !message &&
                !isSalespersonLimitedView &&
                !isCpoEligibilityStale &&
                loanVariables?.is_cpo_eligible &&
                generateFields([APPLY_CPO_CHECKBOX_FIELD])}
            </>
          );

          const labelContent =
            shouldShowGuidebookInfoIcon && typeof inputLabel === 'string' ? (
              <GuidebookLabelWithIcon label={inputLabel} fullGuidebookName={loanVariables?.guidebook} />
            ) : (
              inputLabel
            );

          return (
            <Col className={controlProps?.className as string}>
              <FormRow
                {...fieldWidth}
                key={name}
                required={!!controlProps?.required}
                label={labelContent}
                isVerticalAlign={isVerticalAlign}
                name={name}
                labelWithoutAsterisk={!!controlProps?.labelWithoutAsterisk}
                control={componentContent}
              />
            </Col>
          );
        },
      ),
    [
      mapOnClickFunctionByFieldName,
      mapOptionsByFieldName,
      loanVariables?.guidebook,
      loanVariables?.guidebook_short,
      loanVariables?.ally_cpo,
      loanVariables?.is_cpo_eligible,
      loanVariables?.force_cpo,
      loanVariables?.is_requested_payment,
      isCpoEligibilityStale,
      regionControls,
      isFieldChanged,
      setHistoryCallback,
      getVscLimit,
      getGapLimit,
      t,
      guidebookValue,
      user?.isWithInventory,
      user?.routeOneUserId,
      manuallyCreated,
      isLender,
      lenderControls.maxGap,
      lenderControls.maxVSC,
      maxParticipation,
      getValues,
      availableDealStructureStatus,
      maxPaymentValue,
      payment,
      isSpinerAnimated,
      isTermAnimated,
      isTaxesAnimated,
      isAdjustDealModalAnimated,
      isAddendumActive,
      defaultParticipation,
      dealSourceDataFlags?.isLoading,
      dealSourceData?.finance_details?.vsc_payment_allowance,
      balloonMaxOdometers,
      changeCallback,
      afgTrim,
      balloonVehicleInfo,
      balloonToggle,
      dropdownToggleCallback,
      maxMileage,
      FILTERED_REQUIRED_FIELDS,
      isSalespersonLimitedView,
      shouldHideElement,
      isShopByCallbackMode,
      isAllyLender,
      isAmericreditLender,
      isMtbLender,
      contractDate,
      firstPaymentDaysRange.min,
      firstPaymentDaysRange.max,
      submittedTermMismatch,
    ],
  );

  const generatedFields = useMemo(
    () => ({
      firstRowFields: generateFields(FIRST_MW_ROW_FIELDS),
      secondRowFields: generateFields(SECOND_MW_ROW_FIELDS),
      thirdRowFields: generateFields(isAllyLender ? ALLY_THIRD_MW_ROW_FIELDS : THIRD_MW_ROW_FIELDS),
      balloonFields: generateFields(BALLOON_FIELDS),

      firstRowLabels: generateFields(FIRST_MW_ROW_LABELS),
      secondRowLabels: generateFields(SECOND_MW_ROW_LABELS),
      thirdRowLabels: generateFields(isAllyLender ? ALLY_THIRD_MW_ROW_LABELS : THIRD_MW_ROW_LABELS),
      balloonLabels: generateFields(BALLOON_LABELS),
    }),
    [generateFields, isAllyLender],
  );

  return { generateFields, generatedFields };
};
